'use client';
import { useState } from 'react';

import { getLabelWithEllipsis } from '@core/Utils/Strings/GetLabelWithEllipsis';
import Button from '@gds/Button/Button';
import { Collapse } from '@gds/Collapse/Collapse';
import { Typography } from '@gds/Typography/Typography';
import { PrefetchLink } from '@shared-components/PrefetchLink/PrefetchLink';

import type { CollapsibleInterlinkingProps, LinkBlockItem } from './CollapsibleInterlinking.entity';

import styles from './Collapsiblelnterlinking.module.css';

const LinkBlock = ({ heading, cta }: Pick<LinkBlockItem, 'heading' | 'cta'>) => {
  return (
    <div className={styles.section}>
      {heading ? (
        <Typography className={styles.sectionHeading} Component="h3" variant="subheading2">
          {heading}
        </Typography>
      ) : null}
      <ol className={styles.ctas} aria-label={heading ?? undefined}>
        {cta.map((item, itemIndex) => (
          <li key={`${item.ctaText}-${item.link}-${itemIndex}`} aria-label={item.ctaText}>
            <PrefetchLink href={item.link ?? '/'}>
              {getLabelWithEllipsis(item.ctaText, 30, 27)}
            </PrefetchLink>
          </li>
        ))}
      </ol>
    </div>
  );
};

const LINK_BLOCK_LIMIT = 6;

export const CollapsibleInterlinking = ({ interlinkingItems }: CollapsibleInterlinkingProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      {interlinkingItems.map((interlinkingItem, index) => (
        <div className={styles.container} key={`${interlinkingItem.heading}-${index}`}>
          <Typography className={styles.title} variant="h2">
            {interlinkingItem.heading}
          </Typography>

          <div className={styles.sections}>
            {interlinkingItem.linkBlocks?.slice(0, LINK_BLOCK_LIMIT).map(linkBlock => {
              const visibleOptions = linkBlock.cta.slice(0, LINK_BLOCK_LIMIT);
              const hasMoreOptions = linkBlock.cta.length > LINK_BLOCK_LIMIT;
              const hiddenCtas = hasMoreOptions ? linkBlock.cta.slice(visibleOptions.length) : [];
              return (
                <div
                  key={linkBlock.heading}
                  className={styles.collapsibleSection}
                  {...(linkBlock.dataTestId && { 'data-test-id': linkBlock.dataTestId })}
                >
                  <LinkBlock cta={visibleOptions} heading={linkBlock.heading} />
                  {hasMoreOptions && (
                    <>
                      <Collapse open={isExpanded}>
                        <div className={styles.collapsibleSectionContent}>
                          <LinkBlock
                            key={`${linkBlock.heading}-more`}
                            heading={null}
                            cta={hiddenCtas}
                          />
                        </div>
                      </Collapse>

                      <Button
                        variant="link"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className={styles.viewMoreButton}
                      >
                        {isExpanded ? 'View less' : 'View more'}
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};
